/**
 * 상단 FloatingMenu (P7-②) — 에디터 위에 고정되는 도구 모음.
 * undo/redo · 블록 타입 · B/I/U/S/code · 글자색/배경색 · 이미지/표 삽입.
 * 기존 엔진(applyInlineMark/readActiveMarks, setBlockType) 재사용.
 * 선택 보존: 버튼 mousedown preventDefault → 에디터 blur 방지. 마지막 포커스 .content를 대상으로 적용.
 */

import { applyInlineMark, readActiveMarks } from "../inline-format.js";
import { setBlockType } from "../editing.js";
import { BLOCK_TYPE_DEFS } from "../shared/block-types.js";
import { TEXT_PALETTE, BG_PALETTE } from "../shared/palettes.js";
import { ICON } from "./icons.js";

const MARKS = [
  { mark: "bold", icon: ICON.bold, tkey: "bold", fb: "굵게" },
  { mark: "italic", icon: ICON.italic, tkey: "italic", fb: "기울임" },
  { mark: "underline", icon: ICON.underline, tkey: "underline", fb: "밑줄" },
  { mark: "strike", icon: ICON.strikethrough, tkey: "strike", fb: "취소선" },
  { mark: "code", icon: ICON.code, tkey: "code", fb: "코드" },
];

export function attachFloatingMenu(editorEl, opts) {
  opts = opts || {};
  const undo = typeof opts.undo === "function" ? opts.undo : () => {};
  const redo = typeof opts.redo === "function" ? opts.redo : () => {};
  const canUndo = typeof opts.canUndo === "function" ? opts.canUndo : () => true;
  const canRedo = typeof opts.canRedo === "function" ? opts.canRedo : () => true;
  const onChange = typeof opts.onChange === "function" ? opts.onChange : () => {};
  const insertImage = typeof opts.insertImage === "function" ? opts.insertImage : null;
  const insertTable = typeof opts.insertTable === "function" ? opts.insertTable : null;
  const insertLink = typeof opts.insertLink === "function" ? opts.insertLink : null;
  const cross = opts.cross || null; // 크로스블록 선택 서식 파사드
  const isCross = () => !!(cross && cross.isActive());
  const BT = (opts.locale && opts.locale.blockTypes) || {};
  const TB = (opts.locale && opts.locale.toolbar) || {};
  const headingLevels = Array.isArray(opts.headingLevels) && opts.headingLevels.length ? opts.headingLevels : [1, 2, 3, 4, 5, 6];
  const blockTypeDefs = BLOCK_TYPE_DEFS.filter(([v]) => { const m = v.match(/^heading:(\d)/); return !m || headingLevels.includes(+m[1]); });

  const bar = document.createElement("div");
  bar.className = "lumir-floating-menu";
  bar.setAttribute("data-position", opts.position === "fixed" ? "fixed" : "sticky");
  bar.innerHTML = buildHtml();
  if (editorEl.parentNode) editorEl.parentNode.insertBefore(bar, editorEl);

  function swatch(value, hex, kind) {
    if (value === "default") return `<button type="button" class="lfm-swatch lfm-none" data-color="default" data-kind="${kind}" title="기본"></button>`;
    return `<button type="button" class="lfm-swatch" data-color="${value}" data-kind="${kind}" title="${value}" style="background:${hex}"></button>`;
  }
  function buildHtml() {
    const hist = `<button type="button" class="lfm-btn" data-act="undo" title="실행취소">${ICON.undo}</button><button type="button" class="lfm-btn" data-act="redo" title="다시실행">${ICON.redo}</button>`;
    const type = `<select class="lfm-type" title="블록 유형">` + blockTypeDefs.map(([v, key, fb]) => `<option value="${v}">${BT[key] || fb}</option>`).join("") + `</select>`;
    const marks = MARKS.map((b) => `<button type="button" class="lfm-btn" data-mark="${b.mark}" title="${TB[b.tkey] || b.fb}">${b.icon}</button>`).join("");
    const colors = `<button type="button" class="lfm-btn lfm-color-btn" data-pop="text" title="${TB.textColor || "글자색"}">${ICON.textColor}</button><button type="button" class="lfm-btn lfm-color-btn" data-pop="bg" title="${TB.bgColor || "배경색"}">${ICON.bgColor}</button>`;
    const textPop = `<div class="lfm-pop" data-for="text">` + TEXT_PALETTE.map(([v, h]) => swatch(v, h, "text")).join("") + `</div>`;
    const bgPop = `<div class="lfm-pop" data-for="bg">` + BG_PALETTE.map(([v, h]) => swatch(v, h, "bg")).join("") + `</div>`;
    const insert =
      (insertLink ? `<button type="button" class="lfm-btn" data-act="link" title="${TB.link || "링크"}">${ICON.link}</button>` : "") +
      (insertImage ? `<button type="button" class="lfm-btn" data-act="image" title="이미지">${ICON.image}</button>` : "") +
      (insertTable ? `<button type="button" class="lfm-btn" data-act="table" title="표">${ICON.table}</button>` : "");
    return (
      `<button type="button" class="lfm-btn lfm-toggle" data-act="menutoggle" title="메뉴 펼치기/접기">${ICON.chevronRight}</button>` +
      `<div class="lfm-group">${hist}</div><span class="lfm-sep"></span>` +
      `<div class="lfm-group">${type}</div><span class="lfm-sep"></span>` +
      `<div class="lfm-group">${marks}</div><span class="lfm-sep"></span>` +
      `<div class="lfm-group" style="position:relative">${colors}${textPop}${bgPop}</div>` +
      (insert ? `<span class="lfm-sep"></span><div class="lfm-group">${insert}</div>` : "")
    );
  }

  let activeEditable = null;
  function closestEditable(node) {
    const el = node && node.nodeType === 1 ? node : node && node.parentElement;
    return el ? el.closest(".content, td.lumir-cell") : null;
  }
  function currentBlock() {
    return activeEditable && activeEditable.closest ? activeEditable.closest(".block") : null;
  }
  function closePops() { bar.querySelectorAll(".lfm-pop").forEach((p) => p.classList.remove("open")); }
  function renderActive() {
    const marks = isCross() ? cross.readMarks() : (activeEditable ? readActiveMarks(activeEditable) : {});
    MARKS.forEach((m) => {
      const el = bar.querySelector(`[data-mark="${m.mark}"]`);
      if (el) el.classList.toggle("on", !!marks[m.mark]);
    });
    const sel = bar.querySelector(".lfm-type");
    const b = currentBlock();
    if (sel && b) sel.value = b.dataset.type === "heading" ? "heading:" + (b.dataset.level || "1") + (b.dataset.toggle === "true" ? ":toggle" : "") : b.dataset.type;
    const u = bar.querySelector('[data-act="undo"]');
    const r = bar.querySelector('[data-act="redo"]');
    if (u) u.disabled = !canUndo();
    if (r) r.disabled = !canRedo();
  }
  function trackSelection() {
    const s = document.getSelection();
    if (s && s.rangeCount) {
      const e = closestEditable(s.getRangeAt(0).startContainer);
      if (e && editorEl.contains(e)) activeEditable = e;
    }
    renderActive();
  }

  // 선택 유지(입력/셀렉트 제외)
  bar.addEventListener("mousedown", (e) => { if (e.target.closest("input, select")) return; e.preventDefault(); });

  bar.addEventListener("click", (e) => {
    const act = e.target.closest("[data-act]");
    if (act) {
      const a = act.dataset.act;
      if (a === "menutoggle") { bar.classList.toggle("lfm-expanded"); return; }
      if (a === "undo") { undo(); renderActive(); }
      else if (a === "redo") { redo(); renderActive(); }
      else if (a === "image" && insertImage) insertImage();
      else if (a === "table" && insertTable) insertTable();
      else if (a === "link" && insertLink) insertLink();
      return;
    }
    const mk = e.target.closest("[data-mark]");
    if (mk) {
      if (isCross()) { cross.applyMark(mk.dataset.mark); renderActive(); }
      else if (activeEditable) { applyInlineMark(activeEditable, mk.dataset.mark); onChange(); renderActive(); }
      return;
    }
    const pop = e.target.closest("[data-pop]");
    if (pop) {
      const p = bar.querySelector(`.lfm-pop[data-for="${pop.dataset.pop}"]`);
      const wasOpen = p.classList.contains("open");
      closePops();
      if (!wasOpen) p.classList.add("open");
      return;
    }
    const sw = e.target.closest(".lfm-swatch");
    if (sw) {
      const kind = sw.dataset.kind === "text" ? "textColor" : "backgroundColor";
      if (isCross()) { cross.applyMark(kind, sw.dataset.color); renderActive(); }
      else if (activeEditable) { applyInlineMark(activeEditable, kind, sw.dataset.color); onChange(); renderActive(); }
      closePops();
      return;
    }
  });

  bar.addEventListener("change", (e) => {
    if (!e.target.classList.contains("lfm-type")) return;
    const b = currentBlock();
    if (!b) return;
    const [type, lvl, flag] = e.target.value.split(":");
    const el = setBlockType(b, type, lvl ? parseInt(lvl, 10) : undefined, flag === "toggle");
    activeEditable = el.querySelector(":scope > .content");
    onChange();
    renderActive();
  });

  document.addEventListener("selectionchange", trackSelection);
  editorEl.addEventListener("keyup", trackSelection);
  editorEl.addEventListener("mouseup", trackSelection);
  renderActive();

  // 반응형 3단 레이아웃(P9-⑥): 폭에 따라 compact/tight 클래스 토글
  let ro = null;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver((entries) => {
      const w = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : 9999;
      bar.classList.toggle("lfm-compact", w < 700);   // 2행(wrap)
      const wasMin = bar.classList.contains("lfm-minimized");
      const min = w < 440;
      bar.classList.toggle("lfm-minimized", min);      // 최소화(접기 토글)
      // 모바일 폭 진입 시 기본으로 펼쳐 전체 도구를 노출(접기 토글은 유지 — 사용자가 원하면 다시 접을 수 있음).
      if (min && !wasMin) bar.classList.add("lfm-expanded");
      if (!min) bar.classList.remove("lfm-expanded");
    });
    ro.observe(editorEl);
  }

  return {
    element: bar,
    refresh: renderActive,
    sync: trackSelection,
    destroy() {
      document.removeEventListener("selectionchange", trackSelection);
      editorEl.removeEventListener("keyup", trackSelection);
      editorEl.removeEventListener("mouseup", trackSelection);
      if (ro) ro.disconnect();
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    },
  };
}
